import { router } from "@inertiajs/react";
import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "../../components/ui/button";

interface Props {
  candidateId: number;
  excludedFirms: string[];
}

interface FirmOption { id: number; name: string }

export default function ExclusionsTab({ candidateId, excludedFirms }: Props) {
  const [firmInput, setFirmInput]     = useState("");
  const [firmOptions, setFirmOptions] = useState<FirmOption[]>([]);
  const debounceRef                   = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!firmInput.trim()) { setFirmOptions([]); return; }
    debounceRef.current = setTimeout(() => {
      fetch(`/firms/search?q=${encodeURIComponent(firmInput)}`, {
        headers: { Accept: "application/json" },
      })
        .then((r) => r.json())
        .then((data) => setFirmOptions(data.firms ?? []))
        .catch(() => setFirmOptions([]));
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [firmInput]);

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const name = firmInput.trim();
    if (!name) return;
    router.post(`/candidates/${candidateId}/exclusions`, { firm_name: name }, {
      onSuccess: () => setFirmInput(""),
    });
  }

  function handleRemove(firmName: string) {
    router.delete(`/candidates/${candidateId}/exclusions`, { data: { firm_name: firmName } });
  }

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Excluded firms</h2>
        {excludedFirms.length === 0 ? (
          <p className="text-sm text-gray-400">No firms excluded.</p>
        ) : (
          <ul className="space-y-2">
            {excludedFirms.map((name) => (
              <li key={name} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                <span className="text-gray-800">{name}</span>
                <button
                  onClick={() => handleRemove(name)}
                  className="text-gray-400 hover:text-red-500 transition-colors ml-4"
                  title="Remove exclusion"
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={handleAdd} className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-700">Add exclusion</h2>
        <div className="flex gap-2">
          <input
            list="firm-options"
            value={firmInput}
            onChange={(e) => setFirmInput(e.target.value)}
            placeholder="Firm name…"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <datalist id="firm-options">
            {firmOptions.map((f) => (
              <option key={f.id} value={f.name} />
            ))}
          </datalist>
          <Button type="submit" size="sm" disabled={!firmInput.trim()}>
            Add
          </Button>
        </div>
      </form>
    </div>
  );
}
